/**
 * De eerste inrichting van een nieuwe installatie (`/setup`, publiek;
 * zelfstandig-installeren.md §4.3). Dezelfde stappen als in de desktop- en
 * iPad-app, tegen `/api/v1/setup/*`:
 *
 *   1. setupcode (of setupzin) → setup-token;
 *   2. globale mailinstelling, met testmail;
 *   3. eerste super-admin en workspace;
 *   4. optioneel: Anthropic-sleutel (WIF kan later in Instellingen → AI);
 *   5. inlogcode naar de super-admin, invullen → afgerond en ingelogd.
 *
 * Bij een setupzin rekent deze pagina PBKDF2-HMAC-SHA-512 in de browser uit;
 * de zin zelf gaat nooit naar de server. Het setup-token staat in
 * sessionStorage, zodat herladen niet opnieuw om de (dan al dode) code vraagt.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type Step = "loading" | "closed" | "claim" | "mail" | "admin" | "ai" | "confirm";

interface Status {
  mode: "code" | "phrase";
  state: string;
  expires_at: string | null;
  phrase?: { salt: string; iterations: number };
}

const TOKEN_KEY = "ae.setup.token";

function readToken(): string | null {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) window.sessionStorage.setItem(TOKEN_KEY, token);
    else window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // sessionStorage kan geblokkeerd zijn; dan geldt het token alleen in dit venster.
  }
}

async function call<T>(
  path: string,
  method: string,
  token: string | null,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers["x-setup-token"] = token;
  const res = await fetch(`/api/v1/setup${path}`, {
    method,
    credentials: "include",
    headers,
    body: body === undefined ? null : JSON.stringify(body),
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok) {
    const err = new Error(String(json.detail ?? json.title ?? `HTTP ${res.status}`)) as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  return json as T;
}

/** PBKDF2-HMAC-SHA-512 van de zin, als hex — de "proof" voor de server. */
async function phraseProof(
  phrase: string,
  saltBase64: string,
  iterations: number,
): Promise<string> {
  const salt = Uint8Array.from(atob(saltBase64), (ch) => ch.charCodeAt(0));
  const base = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(phrase),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-512", salt, iterations },
    base,
    512,
  );
  return [...new Uint8Array(bits)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

const input =
  "rounded border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50";
const primary =
  "rounded bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-700 disabled:opacity-50";
const secondary =
  "rounded border border-slate-300 bg-white px-4 py-2 font-medium text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50";

export function SetupPage() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState<Step>("loading");
  const [status, setStatus] = useState<Status | null>(null);
  const [token, setToken] = useState<string | null>(readToken);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [secret, setSecret] = useState("");
  const [mail, setMail] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    from_address: "",
    from_name: "",
  });
  const [testTo, setTestTo] = useState("");
  const [admin, setAdmin] = useState({
    email: "",
    workspace_name: "",
    // De modeltaal volgt standaard de taal van de interface.
    default_language: i18n.language.startsWith("nl") ? "nl" : "en",
  });
  const [apiKey, setApiKey] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await fn();
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 401 && token) {
        writeToken(null);
        setToken(null);
      }
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const s = await call<Status>("/status", "GET", null);
      setStatus(s);
      const saved = readToken();
      setStep(saved ? "mail" : "claim");
    } catch (e) {
      if ((e as { status?: number }).status === 404) setStep("closed");
      else setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const claim = () =>
    run(async () => {
      if (!status) return;
      const body =
        status.mode === "phrase" && status.phrase
          ? { proof: await phraseProof(secret, status.phrase.salt, status.phrase.iterations) }
          : { code: secret };
      const res = await call<{ token: string }>("/claim", "POST", null, body);
      writeToken(res.token);
      setToken(res.token);
      setSecret("");
      setStep("mail");
    });

  const saveMail = () =>
    run(async () => {
      await call("/mail", "PUT", token, {
        ...mail,
        from_name: mail.from_name || null,
        transport: "smtp",
      });
      setInfo(t("setup.saved"));
      setStep("admin");
    });

  const testMail = () =>
    run(async () => {
      const res = await call<{ recipient: string }>("/mail/test", "POST", token, { to: testTo });
      setInfo(t("setup.mail.testSent", { to: res.recipient }));
    });

  const saveAdmin = () =>
    run(async () => {
      await call("/admin", "PUT", token, admin);
      setStep("ai");
    });

  const saveAi = (skip: boolean) =>
    run(async () => {
      if (!skip) await call("/ai", "PUT", token, { api_key: apiKey });
      setApiKey("");
      const res = await call<{ sent_to: string }>("/complete", "POST", token, {});
      setSentTo(res.sent_to);
      setStep("confirm");
    });

  const resend = () =>
    run(async () => {
      const res = await call<{ sent_to: string }>("/complete", "POST", token, {});
      setSentTo(res.sent_to);
      setInfo(t("setup.confirm.resent"));
    });

  const confirm = () =>
    run(async () => {
      await call("/confirm", "POST", token, { code: code.trim() });
      writeToken(null);
      nav("/", { replace: true });
      window.location.reload();
    });

  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-100 px-4 py-12">
      <div className="w-full max-w-lg rounded-lg border border-slate-300 bg-white p-6 shadow">
        <h1 className="font-semibold text-slate-900 text-xl">{t("setup.title")}</h1>
        <p className="mt-1 text-slate-600 text-sm">{t("setup.intro")}</p>

        {step === "loading" && <p className="mt-6 text-slate-500 text-sm">…</p>}

        {step === "closed" && (
          <div className="mt-6 flex flex-col gap-3">
            <p className="text-slate-700 text-sm">{t("setup.closed")}</p>
            <button type="button" className={primary} onClick={() => nav("/login")}>
              {t("setup.toLogin")}
            </button>
          </div>
        )}

        {step === "claim" && status && (
          <form
            className="mt-6 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void claim();
            }}
          >
            {status.state !== "open" && (
              <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 text-sm">
                {t(`setup.refused.${status.state}`, { defaultValue: status.state })}
              </p>
            )}
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-slate-700">
                {status.mode === "phrase" ? t("setup.claim.phrase") : t("setup.claim.code")}
              </span>
              <input
                className={`${input} ${status.mode === "code" ? "font-mono uppercase tracking-widest" : ""}`}
                type={status.mode === "phrase" ? "password" : "text"}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={status.mode === "code" ? "XXXX-XXXX-XXXX-XXXX" : ""}
                autoComplete="off"
                spellCheck={false}
                disabled={busy}
                // biome-ignore lint/a11y/noAutofocus: enige veld op een lege pagina
                autoFocus
              />
            </label>
            {status.expires_at && (
              <p className="text-slate-500 text-xs">
                {t("setup.claim.expires", { date: new Date(status.expires_at).toLocaleString() })}
              </p>
            )}
            <button type="submit" className={primary} disabled={busy || !secret.trim()}>
              {busy && status.mode === "phrase" ? t("setup.claim.computing") : t("setup.next")}
            </button>
          </form>
        )}

        {step === "mail" && (
          <form
            className="mt-6 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void saveMail();
            }}
          >
            <h2 className="font-semibold text-slate-800">{t("setup.mail.title")}</h2>
            <p className="text-slate-600 text-sm">{t("setup.mail.intro")}</p>
            <div className="grid grid-cols-[1fr_6rem] gap-3">
              <Field label={t("setup.mail.host")}>
                <input
                  className={input}
                  value={mail.host}
                  onChange={(e) => setMail({ ...mail, host: e.target.value })}
                  disabled={busy}
                />
              </Field>
              <Field label={t("setup.mail.port")}>
                <input
                  className={input}
                  type="number"
                  value={mail.port}
                  onChange={(e) => setMail({ ...mail, port: Number(e.target.value) || 587 })}
                  disabled={busy}
                />
              </Field>
            </div>
            <Field label={t("setup.mail.username")}>
              <input
                className={input}
                value={mail.username}
                onChange={(e) => setMail({ ...mail, username: e.target.value })}
                autoComplete="off"
                disabled={busy}
              />
            </Field>
            <Field label={t("setup.mail.password")}>
              <input
                className={input}
                type="password"
                value={mail.password}
                onChange={(e) => setMail({ ...mail, password: e.target.value })}
                autoComplete="new-password"
                disabled={busy}
              />
            </Field>
            <Field label={t("setup.mail.fromAddress")}>
              <input
                className={input}
                type="email"
                value={mail.from_address}
                onChange={(e) => setMail({ ...mail, from_address: e.target.value })}
                disabled={busy}
              />
            </Field>
            <Field label={t("setup.mail.fromName")}>
              <input
                className={input}
                value={mail.from_name}
                onChange={(e) => setMail({ ...mail, from_name: e.target.value })}
                disabled={busy}
              />
            </Field>
            <button
              type="submit"
              className={primary}
              disabled={busy || !mail.host.trim() || !mail.from_address.trim()}
            >
              {t("setup.saveAndNext")}
            </button>
            <div className="mt-2 flex gap-2 border-slate-200 border-t pt-3">
              <input
                className={`${input} flex-1`}
                type="email"
                placeholder={t("setup.mail.testTo")}
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                disabled={busy}
              />
              <button
                type="button"
                className={secondary}
                disabled={busy || !testTo.trim()}
                onClick={() => void testMail()}
              >
                {t("setup.mail.test")}
              </button>
            </div>
            <p className="text-slate-500 text-xs">{t("setup.mail.testHint")}</p>
          </form>
        )}

        {step === "admin" && (
          <form
            className="mt-6 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void saveAdmin();
            }}
          >
            <h2 className="font-semibold text-slate-800">{t("setup.admin.title")}</h2>
            <p className="text-slate-600 text-sm">{t("setup.admin.intro")}</p>
            <Field label={t("setup.admin.email")}>
              <input
                className={input}
                type="email"
                value={admin.email}
                onChange={(e) => setAdmin({ ...admin, email: e.target.value })}
                disabled={busy}
              />
            </Field>
            <Field label={t("setup.admin.workspaceName")}>
              <input
                className={input}
                value={admin.workspace_name}
                onChange={(e) => setAdmin({ ...admin, workspace_name: e.target.value })}
                disabled={busy}
              />
            </Field>
            <Field label={t("setup.admin.language")}>
              <select
                className={input}
                value={admin.default_language}
                onChange={(e) => setAdmin({ ...admin, default_language: e.target.value })}
                disabled={busy}
              >
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
              </select>
            </Field>
            <div className="flex gap-2">
              <button type="button" className={secondary} onClick={() => setStep("mail")}>
                {t("setup.back")}
              </button>
              <button
                type="submit"
                className={primary}
                disabled={busy || !admin.email.trim() || !admin.workspace_name.trim()}
              >
                {t("setup.saveAndNext")}
              </button>
            </div>
          </form>
        )}

        {step === "ai" && (
          <form
            className="mt-6 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void saveAi(false);
            }}
          >
            <h2 className="font-semibold text-slate-800">{t("setup.ai.title")}</h2>
            <p className="text-slate-600 text-sm">{t("setup.ai.intro")}</p>
            <Field label={t("settings.aiAuth.keyTitle")}>
              <input
                className={`${input} font-mono`}
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-ant-…"
                autoComplete="off"
                disabled={busy}
              />
            </Field>
            <div className="flex gap-2">
              <button type="button" className={secondary} onClick={() => setStep("admin")}>
                {t("setup.back")}
              </button>
              <button
                type="button"
                className={secondary}
                disabled={busy}
                onClick={() => void saveAi(true)}
              >
                {t("setup.ai.skip")}
              </button>
              <button type="submit" className={primary} disabled={busy || !apiKey.trim()}>
                {t("setup.saveAndNext")}
              </button>
            </div>
          </form>
        )}

        {step === "confirm" && (
          <form
            className="mt-6 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void confirm();
            }}
          >
            <h2 className="font-semibold text-slate-800">{t("setup.confirm.title")}</h2>
            <p className="text-slate-600 text-sm">
              {t("setup.confirm.intro", { email: sentTo ?? admin.email })}
            </p>
            <input
              className={`${input} font-mono tracking-widest`}
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              disabled={busy}
            />
            <div className="flex gap-2">
              <button
                type="button"
                className={secondary}
                disabled={busy}
                onClick={() => void resend()}
              >
                {t("setup.confirm.resend")}
              </button>
              <button type="submit" className={primary} disabled={busy || code.length !== 6}>
                {t("setup.confirm.finish")}
              </button>
            </div>
          </form>
        )}

        {err && (
          <p className="mt-4 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-900 text-sm">
            {err}
          </p>
        )}
        {info && (
          <p className="mt-4 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-emerald-900 text-sm">
            {info}
          </p>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
    </label>
  );
}
